import React, { useState, useEffect, useRef } from "react";
import {
  FaMapMarkerAlt,
  FaStar,
  FaArrowRight,
  FaPlane,
  FaGlobeAmericas,
  FaUserFriends,
  FaHeart,
  FaRegHeart,
} from "react-icons/fa";
import "../Vender/css/LuxuryDestinations.css"; // Ensure this matches your CSS file name

const LuxuryDestinations = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [isVisible, setIsVisible] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const sectionRef = useRef(null);

  const categories = [
    { id: "all", name: "All Destinations" },
    { id: "beach", name: "Beach" },
    { id: "mountain", name: "Mountain" },
    { id: "heritage", name: "Heritage" },
    { id: "nature", name: "Nature" },
  ];

  const destinations = [
    {
      id: 1,
      name: "The Maldives",
      category: "beach",
      image: "/assets/images/maldives.jpg",
      location: "Indian Ocean",
      rating: 4.9,
      price: 89999,
      description: "Pristine white beaches and turquoise waters.",
      color: "#06b6d4", // Cyan accent
    },
    {
      id: 2,
      name: "Dubai",
      category: "city",
      image: "/assets/images/dubai-bg.jpeg",
      location: "United Arab Emirates",
      rating: 4.7,
      price: 99999,
      description:
        "Luxury city with iconic skyline, desert safaris, and world-class shopping.",
      color: "#f59e0b", // Amber / desert accent
    },
    {
      id: 3,
      name: "Rajasthan",
      category: "heritage",
      image: "/assets/images/jaipur-bg.jpeg",
      location: "India",
      rating: 4.7,
      price: 45999,
      description: "Royal palaces and vibrant culture.",
      color: "#f59e0b", // Amber accent
    },
    {
      id: 4,
      name: "Kerala",
      category: "nature",
      image: "/assets/images/south-india.jpg",
      location: "South India",
      rating: 4.6,
      price: 35999,
      description: "Serene backwaters and lush tea gardens.",
      color: "#10b981", // Emerald accent
    },
    {
      id: 7,
      name: "Andaman & Nicobar",
      category: "beach",
      image: "/assets/images/andamannikobar-bg.jpg",
      location: "South India",
      rating: 4.8,
      price: 45999,
      description:
        "Pristine islands with crystal clear waters and coral reefs.",
      color: "#06b6d4", // Cyan accent
    },
    {
      id: 5,
      name: "Bali",
      category: "beach",
      image: "/assets/images/banner-img2-beach.jpg",
      location: "Indonesia",
      rating: 4.8,
      price: 65999,
      description: "Tropical paradise with ancient temples.",
      color: "#8b5cf6", // Violet accent
    },
    {
      id: 6,
      name: "Himalayas",
      category: "mountain",
      image: "/assets/images/banner-img1-mountain.jpg",
      location: "North India",
      rating: 4.7,
      price: 55999,
      description: "Peaceful retreats in the mighty mountains.",
      color: "#ec4899", // Pink accent
    },
  ];

  const filteredDestinations =
    activeCategory === "all"
      ? destinations
      : destinations.filter((dest) => dest.category === activeCategory);

  const toggleFavorite = (e, id) => {
    e.stopPropagation();
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((fav) => fav !== id) : [...prev, id],
    );
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  return (
    <section
      className={`membership-section ${isVisible ? "is-visible" : ""}`}
      ref={sectionRef}
    >
      {/* Background Elements */}
      <div className="membership-bg">
        <div className="bg-gradient"></div>
        <div className="bg-pattern"></div>
        <div className="floating-shapes">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      <div className="membership-wrapper">
        {/* Header */}
        <header className="membership-header">
          <div className="header-badge">
            <span className="badge-dot"></span>
            World Class Travel
          </div>
          <h2 className="header-title">
            Discover Your Next <br />
            <span className="title-highlight">Dream Destination</span>
          </h2>
          <p className="header-subtitle">
            Experience the extraordinary with our handpicked collection of
            luxury destinations, curated for the discerning traveler.
          </p>
        </header>

        {/* Stats Bar (Replaces old Slider) */}
        <div className="stats-bar">
          <div className="stat-item" style={{ "--index": 1 }}>
            <div className="stat-value">150+</div>
            <div className="stat-label">Destinations</div>
            <div className="stat-divider"></div>
          </div>
          <div className="stat-item" style={{ "--index": 2 }}>
            <div className="stat-value">50k+</div>
            <div className="stat-label">Happy Travelers</div>
            <div className="stat-divider"></div>
          </div>
          <div className="stat-item" style={{ "--index": 3 }}>
            <div className="stat-value">4.9</div>
            <div className="stat-label">Average Rating</div>
            <div className="stat-divider"></div>
          </div>
          <div className="stat-item" style={{ "--index": 4 }}>
            <div className="stat-value">24/7</div>
            <div className="stat-label">Support</div>
          </div>
        </div>

        {/* Custom Filters styled to match theme */}
        <div className="category-filter-container">
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`filter-btn ${activeCategory === cat.id ? "active" : ""}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="benefits-grid">
          {filteredDestinations.map((dest, index) => (
            <div
              key={dest.id}
              className="benefit-card"
              style={{ "--delay": `${index * 0.1}s`, "--accent": dest.color }}
            >
              <div className="card-border"></div>
              <div className="card-glow"></div>

              <div className="card-content">
                {/* Image acting as the "Icon" */}
                <div className="card-icon-wrapper">
                  <div className="card-icon image-mode">
                    <img src={dest.image} alt={dest.name} />
                  </div>
                  <div className="icon-ring"></div>
                </div>

                <div className="card-info">
                  <div className="card-header-row">
                    <h3 className="card-title">{dest.name}</h3>
                    <div
                      className="fav-icon"
                      onClick={(e) => toggleFavorite(e, dest.id)}
                    >
                      {favorites.includes(dest.id) ? (
                        <FaHeart style={{ color: "#ef4444" }} />
                      ) : (
                        <FaRegHeart />
                      )}
                    </div>
                  </div>

                  <div className="card-meta">
                    <span>
                      <FaMapMarkerAlt /> {dest.location}
                    </span>
                    <span>
                      <FaStar className="star" /> {dest.rating}
                    </span>
                  </div>

                  <p className="card-desc">{dest.description}</p>

                  {/* <div className="card-price">
                    Starting from ₹{dest.price.toLocaleString()}
                  </div> */}
                </div>

                {/* <div className="card-arrow">
                  <FaArrowRight />
                </div> */}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="membership-cta">
          <a href="/travelthemes" className="cta-button primary">
            <span className="btn-text">Start Your Journey</span>
            <div className="btn-icon">
              <FaPlane />
            </div>
            <div className="btn-shine"></div>
          </a>
          <a href="/ourproduct" className="cta-button secondary">
            <div className="btn-icon-left">
              <FaGlobeAmericas />
            </div>
            <span>View All Packages</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default LuxuryDestinations;
